// posSlice.js
import { createSlice } from "@reduxjs/toolkit";

const getInitialState = () => ({
    cart: [],
    selectedUnits: {},
    contactId: "",
    contactName: "",
    contactNumber: "",
    billingAddressId: "",
    shippingAddressId: "",
    sameAsBilling: true,
    addCustomerDetails: false,
    selectedCompany: "",
    selectedBranch: "",
    searchQuery: "",
    searchBarcode: "",
    showBillingModal: false,
    showShippingModal: false,
    isInvoiceSaved: false,
    invoiceNo: "",
    invoiceFullNo: "",
    selectedCategory: "",
    selectedMaincategory: "ALL",
    categorySearchQuery: "",
    subTArray: [],
    showQtyRed: false,
    saveBtn: false,
    printBtn: true,
    whatsappBtn: true,
});

const posSlice = createSlice({
    name: "pos",
    initialState: getInitialState(),
    reducers: {
        // Cart actions
        setCart: (state, action) => {
            state.cart = action.payload;
        },
        addToCart: (state, action) => {
            state.cart.push(action.payload);
        },
        updateCartItem: (state, action) => {
            const { index, updates } = action.payload;
            if (state.cart[index]) {
                state.cart[index] = { ...state.cart[index], ...updates };
            }
        },
        // In posSlice.js, update the removeFromCart reducer:
        removeFromCart: (state, action) => {
            const index = action.payload;
            // Create a new array without the item
            const newCart = state.cart.filter((_, i) => i !== index);
            state.cart = newCart;

            // Also remove from selectedUnits
            const newSelectedUnits = { ...state.selectedUnits };
            delete newSelectedUnits[index];

            // Reindex selectedUnits
            const reindexedSelectedUnits = {};
            Object.keys(newSelectedUnits).forEach((key, newIndex) => {
                const keyNum = parseInt(key);
                if (keyNum > index) {
                    reindexedSelectedUnits[keyNum - 1] = newSelectedUnits[key];
                } else if (keyNum < index) {
                    reindexedSelectedUnits[keyNum] = newSelectedUnits[key];
                }
            });
            state.selectedUnits = reindexedSelectedUnits;
        },
        clearCart: (state) => {
            state.cart = [];
            state.selectedUnits = {};
            state.subTArray = [];
        },

        // Units management
        setSelectedUnits: (state, action) => {
            state.selectedUnits = action.payload;
        },
        updateSelectedUnit: (state, action) => {
            const { index, unit } = action.payload;
            state.selectedUnits[index] = unit;
        },

        // Contact details
        setContactDetails: (state, action) => {
            const { id, name, number } = action.payload;
            state.contactId = id;
            state.contactName = name;
            state.contactNumber = number;
        },

        // Address details
        setAddressDetails: (state, action) => {
            const { billingAddressId, shippingAddressId, sameAsBilling } = action.payload;
            state.billingAddressId = billingAddressId;
            state.shippingAddressId = shippingAddressId;
            state.sameAsBilling = sameAsBilling;
        },

        // Customer details toggle
        setAddCustomerDetails: (state, action) => {
            state.addCustomerDetails = action.payload;
        },

        // Company & Branch
        setSelectedCompany: (state, action) => {
            state.selectedCompany = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },

        // Search
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        setSearchBarcode: (state, action) => {
            state.searchBarcode = action.payload;
        },

        // Modals
        setShowBillingModal: (state, action) => {
            state.showBillingModal = action.payload;
        },
        setShowShippingModal: (state, action) => {
            state.showShippingModal = action.payload;
        },

        // Invoice
        setIsInvoiceSaved: (state, action) => {
            state.isInvoiceSaved = action.payload;
        },
        setInvoiceDetails: (state, action) => {
            const { invoiceNo, invoiceFullNo } = action.payload;
            state.invoiceNo = invoiceNo;
            state.invoiceFullNo = invoiceFullNo;
        },

        // Categories
        setSelectedCategory: (state, action) => {
            state.selectedCategory = action.payload;
        },
        setSelectedMaincategory: (state, action) => {
            state.selectedMaincategory = action.payload;
        },
        setCategorySearchQuery: (state, action) => {
            state.categorySearchQuery = action.payload;
        },

        // Calculations
        setSubTArray: (state, action) => {
            state.subTArray = action.payload;
        },

        // UI States
        setShowQtyRed: (state, action) => {
            state.showQtyRed = action.payload;
        },
        setSaveBtn: (state, action) => {
            state.saveBtn = action.payload;
        },
        setPrintBtn: (state, action) => {
            state.printBtn = action.payload;
        },
        setWhatsappBtn: (state, action) => {
            state.whatsappBtn = action.payload;
        },

        // Reset everything
        resetPosState: () => getInitialState(),

        // Reset for new invoice
        // In your posSlice.js
        // In posSlice.js - update resetForNewInvoice action
        resetForNewInvoice: (state) => {
            state.cart = [];
            state.selectedUnits = {};
            state.subTArray = [];
            state.contactId = "";
            state.contactName = "";
            state.contactNumber = "";
            state.billingAddressId = "";
            state.shippingAddressId = "";
            state.sameAsBilling = false; // ✅ Change from true to false
            state.addCustomerDetails = false;
            state.showBillingModal = false;
            state.showShippingModal = false;
            state.isInvoiceSaved = false;
            state.saveBtn = false;
            state.printBtn = true;
            state.whatsappBtn = true;
            state.showQtyRed = false;
            // Keep other states as needed
        }
    },
});

export const {
    setCart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    setSelectedUnits,
    updateSelectedUnit,
    setContactDetails,
    setAddressDetails,
    setAddCustomerDetails,
    setSelectedCompany,
    setSelectedBranch,
    setSearchQuery,
    setSearchBarcode,
    setShowBillingModal,
    setShowShippingModal,
    setIsInvoiceSaved,
    setInvoiceDetails,
    setSelectedCategory,
    setSelectedMaincategory,
    setCategorySearchQuery,
    setSubTArray,
    setShowQtyRed,
    setSaveBtn,
    setPrintBtn,
    setWhatsappBtn,
    resetPosState,
    resetForNewInvoice,
} = posSlice.actions;

export default posSlice.reducer;